import React, { useState } from 'react';
import { Button, Drawer, Table, useToasts } from 'freemium-ui';
import { History } from 'history';
import { get, omit, uniq } from 'lodash';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { COTACTS_PER_PAGE, ON_CLICK_OPEN_COLUMNS } from 'routes/constants';
import { useGetAllUsers } from 'routes/screens/settings/screens/user/hooks';
import { SelectAllContainer, TableDataContainer } from 'routes/styles';
import EmptyState from 'shared/components/EmptyState';
import { BACK_END_FIELDS, COLUMNS_HIDE_ORDERING_KEY } from 'shared/constants';
import { useExecuteSegment } from 'shared/hooks';
import { FreshLink } from 'shared/styles';
import {
  AccountInfoType,
  AllUsersType,
  BreadcrumpDataType,
  ConditionType,
  ConfirmationType,
  ContactFormType,
  ContactListType,
  FilterViewType,
  FormServFieldType,
  McrContactType,
  MetaType,
  SegmentResponseType,
  UserType,
} from 'shared/types';
import { getAllUsersFromResponse, getDefaultColumnOrder } from 'shared/utils';
import { useAccessControl } from 'utils/access-control';
import Filters from '../../../components/Filters';
import HeaderComponent from '../../../components/HeaderComponent';
import NewContactForm from '../../../components/NewButton/NewContactForm';
import ContactsDefaultFilterView from '../components/FilterViews';
import {
  useDeleteFilterView,
  useGetFilterView,
  useGetFilterViews,
  useSaveFilterView,
} from '../components/FilterViews/hooks';
import { useLocalStorage } from '../../../../hooks';
import { useDeleteContact, useSaveContact } from '../hooks';
import {
  getSegmentPayloadFromFilters,
  getTableColumnsFromFields,
} from '../../../utils';
import { getPayloadForLocalState } from './utils';
import {
  DefaultFilterViews,
  getContactMenuOptions,
  getContactsColumnSettings,
} from '../constants';

export const getDefaultCondition: (mcrAccountId: string) => ConditionType = (
  mcrAccountId,
) => ({
  name: 'account_id',
  evaluate_on: 'CONTACT',
  operator: 'IS',
  field_type: 'number',
  value: mcrAccountId ? String(mcrAccountId) : '',
});

type ContactsProps = {
  setBreadcrumpData?: (
    data: BreadcrumpDataType | React.SetStateAction<BreadcrumpDataType>,
  ) => void;
  setConfirmationData: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
  contactForm: ContactFormType;
  actionTime?: string;
  contactLists: ContactListType[];
  userInfo: UserType;
  contactsCount: number;
  fetchContactCount: () => void;
};

const Contacts = (props: ContactsProps) => {
  const {
    contactForm,
    contactLists,
    actionTime,
    setConfirmationData,
    userInfo,
    contactsCount,
    fetchContactCount,
  } = props;
  const { t } = useTranslation();
  const [, setToast] = useToasts();
  const { push }: History = useHistory();
  const queryClient = useQueryClient();
  const { access = [] } = useAccessControl();
  const accountInfo: AccountInfoType | undefined =
    queryClient.getQueryData('accountinfo');

  const fields = React.useMemo(
    () =>
      contactForm?.fields?.filter(
        (contact) => !BACK_END_FIELDS.includes(contact.name),
      ) || [],
    [contactForm],
  );
  const allFields = React.useMemo<Record<string, FormServFieldType>>(() => {
    if (!fields) return {};
    const newAllFields = {};
    fields.forEach((field) => {
      newAllFields[field[COLUMNS_HIDE_ORDERING_KEY]] = field;
    });
    return newAllFields;
  }, [fields]);
  const [shouldFetchAllUsers, setShouldFetchAllUsers] =
    React.useState<boolean>(false);
  const { data } = useGetAllUsers({
    enabled: shouldFetchAllUsers,
  });
  const allUsers: AllUsersType = getAllUsersFromResponse(data);
  const columnSettings = React.useMemo(
    () =>
      getContactsColumnSettings({ allUsers, fields, accountInfo, userInfo }),
    [allUsers, fields, accountInfo, userInfo],
  );
  const columns = React.useMemo(
    () =>
      getTableColumnsFromFields({
        fields,
        contactLists,
        columnSettings,
        accountInfo,
      }),
    [fields, contactLists, columnSettings, accountInfo],
  );
  const [segmentData, setSegmentData] = React.useState<any>();
  const [allContacts, setAllContacts] = React.useState<
    Record<string, McrContactType>
  >({});
  const [meta, setMeta] = React.useState<MetaType>({});
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  const [isAllSelected, setAllSelected] = React.useState<boolean>(false);
  const [filterViewCount, setFilterViewCount] = React.useState<number>();
  const [editData, setEditData] = React.useState<any>({});
  const [filter, setFilter] = useLocalStorage('contacts-filter', {});
  const [filterViewId, setFilterViewId] = useLocalStorage(
    'contacts-default-filter-view',
    'all',
  );
  const [pageIndex, setPageIndex] = React.useState<number>(0);
  const [selectedRowIds, setSelectedRowIds] = React.useState<
    Record<string, boolean>
  >({});
  const [columnOrder, setColumnOrder] = useLocalStorage(
    'contacts-column-order',
    getDefaultColumnOrder({
      fields,
      columnSettings,
    }),
  );
  const [isFilterConditionsModified, setisFilterConditionsModified] =
    useState<boolean>(false);
  const [selectedFilter, setselectedFilter] = useState<FilterViewType>({
    id: '',
    name: '',
    conditions: [],
  });

  const [hiddenColumns, setHiddenColumns] = useLocalStorage(
    'contacts-hidden-columns',
    fields
      .filter(
        (field: FormServFieldType) =>
          !(
            columnSettings[field.name]?.showInitial ||
            columnSettings[field.name]?.disableReordering
          ),
      )
      .map((field) => field[COLUMNS_HIDE_ORDERING_KEY]),
  );

  const rule = React.useMemo(
    () =>
      getSegmentPayloadFromFilters(
        filter,
        getDefaultCondition(accountInfo?.mcr_account_id || ''),
        filterViewId,
      ),
    [filter, filterViewId, accountInfo],
  );
  const segmentRule = React.useMemo(
    () => getSegmentPayloadFromFilters(filter),
    [filter],
  );
  const {
    mutate: executeSegment,
    isLoading: isExecutingSegment,
    isError: isExecuteSegmentError,
  } = useExecuteSegment({
    setToast,
    callback: (isSuccess, response: SegmentResponseType) => {
      if (isSuccess) {
        if (response.contacts) {
          setAllContacts((old) => {
            const newValues = { ...old };
            response.contacts.forEach((c: McrContactType) => {
              newValues[c.id || ''] = c;
            });
            return newValues;
          });
          if (isAllSelected) {
            setSelectedRowIds((old) => {
              const newValues = { ...old };
              response.contacts.forEach((c: McrContactType) => {
                if (c.id) {
                  newValues[c.id] = true;
                }
              });
              return newValues;
            });
          }
          setSegmentData(response.contacts);
        }

        const count = get(response, ['meta', 'totalNumberOfRecords'], 0);
        setFilterViewCount(count);
        fetchContactCount();
        if (window && window.heap) {
          window.heap.addUserProperties({
            contacts_count: count,
          });
        }

        if (response?.meta) setMeta(response.meta);
      }
    },
  });

  const { mutate: saveContact } = useSaveContact({
    setToast,
    callback: (isSuccess) => {
      if (isSuccess) {
        setConfirmationData({});
        executeSegment({
          rule: getSegmentPayloadFromFilters(
            filter,
            getDefaultCondition(accountInfo?.mcr_account_id || ''),
            filterViewId,
          ),
          params: {
            page: pageIndex + 1,
            per_page: COTACTS_PER_PAGE,
          },
        });
      } else {
        setConfirmationData((old) => ({
          ...old,
          confirmButtonDisabled: false,
        }));
        setToast({
          type: 'error',
          delay: 3000,
          text: `${t('forms.toast.error.contact_update')}`,
        });
      }
    },
  });

  const { mutate: deleteContact } = useDeleteContact({
    setToast,
    callback: (isSuccess: boolean, _response: any, cId: string) => {
      if (isSuccess) {
        setSelectedRowIds(omit(selectedRowIds, cId));
        setConfirmationData({});
        executeSegment({
          rule: getSegmentPayloadFromFilters(
            filter,
            getDefaultCondition(accountInfo?.mcr_account_id || ''),
            filterViewId,
          ),
          params: {
            page: count % COTACTS_PER_PAGE > 1 ? pageIndex + 1 : 1,
            per_page: COTACTS_PER_PAGE,
          },
          callback: (isSuccess: boolean) => {
            if (isSuccess && count % COTACTS_PER_PAGE <= 1) {
              setPageIndex(0);
            }
          },
        });
      } else {
        setConfirmationData((old) => ({
          ...old,
          confirmButtonDisabled: false,
        }));
      }
    },
  });

  const onDefaultFilterViewChange = (val: FilterViewType, refetch = true) => {
    const newFilter = getPayloadForLocalState(val);
    setselectedFilter({ ...val, conditions: val.conditions });
    setFilter(newFilter);
    setFilterViewId(val.id);
    if (refetch) {
      executeSegment({
        rule: getSegmentPayloadFromFilters(
          newFilter,
          getDefaultCondition(accountInfo?.mcr_account_id || ''),
          val.id,
        ),
        params: {
          page: 1,
          per_page: COTACTS_PER_PAGE,
        },
        callback: (isSuccess: boolean) => {
          if (isSuccess) {
            setPageIndex(0);
          }
        },
      });
    }
  };

  const clearFilterView = (selectedFilter?: FilterViewType) => {
    const newFilter = getPayloadForLocalState(selectedFilter);
    setFilter(newFilter);
    executeSegment({
      rule: getSegmentPayloadFromFilters(
        newFilter,
        getDefaultCondition(accountInfo?.mcr_account_id || ''),
        selectedFilter?.id,
      ),
      params: {
        page: 1,
        per_page: COTACTS_PER_PAGE,
      },
      callback: (isSuccess: boolean) => {
        if (isSuccess) {
          setPageIndex(0);
        }
      },
    });
  };

  const onDeleteFilterView = (filterId: string, filterName: string) => {
    setConfirmationData({
      id: new Date().toISOString(),
      title: `${t('forms.confirmation.delete_filter_view')}`,
      className: 'error',
      confirmButtonText: `${t('confirm')}`,
      onConfirm: () => {
        setConfirmationData((old) => ({
          ...old,
          confirmButtonDisabled: true,
        }));
        deleteFilterView(filterId);
      },
      fill: '#d7461f',
      infoText: '',
      description: `${t('forms.confirmation.confirm_filter_view_delete', {
        filterName: filterName,
      })}`,
      glyph: 'info',
    });
  };
  const { mutate: deleteFilterView } = useDeleteFilterView({
    setToast,
    callback: (isSuccess: boolean) => {
      if (isSuccess) {
        queryClient.invalidateQueries('filters-contact');
        setConfirmationData({});
        onDefaultFilterViewChange(DefaultFilterViews[0]);
        setFilter({});
        setFilterViewId(null);
      }
    },
  });

  useGetFilterView({
    callback: (isSuccess: boolean, response: FilterViewType) => {
      if (isSuccess) {
        setselectedFilter(response);
      } else {
        setToast({
          type: 'error',
          delay: 3000,
          text: t('empty_state.message.network_error'),
        });
      }
    },
    filterViewId: DefaultFilterViews.some((fView) => filterViewId === fView.id)
      ? null
      : filterViewId,
  });

  const { mutate: onSaveFilterView } = useSaveFilterView({
    setToast,
    callback: (
      isSuccess: boolean,
      response: FilterViewType,
      isCreated: boolean,
    ) => {
      if (isSuccess) {
        if (isCreated) {
          queryClient.invalidateQueries('filters-contact');
        }
        onDefaultFilterViewChange(
          {
            ...response,
            conditions: Object.values(response?.conditions),
          },
          false,
        );
      }
    },
  });

  const onFiltersApply = (filterData?: any) => {
    const ruleToExecute = getSegmentPayloadFromFilters(
      filterData || filter,
      getDefaultCondition(accountInfo?.mcr_account_id || ''),
      filterViewId,
    );
    executeSegment({
      rule: ruleToExecute,
      params: {
        page: 1,
        per_page: COTACTS_PER_PAGE,
      },
      callback: (isSuccess: boolean) => {
        if (isSuccess) {
          setPageIndex(0);
          setSelectedRowIds({});
          const neededColumns: string[] = [];
          ruleToExecute?.condition_sets.forEach((condition_set) => {
            if (condition_set?.conditions.length)
              condition_set.conditions.forEach((condition) => {
                if (condition.name) neededColumns.push(condition.name);
              });
          });
          let newColumnOrder: string[] = [...(columnOrder || [])];
          const newHiddenColumns: string[] = (hiddenColumns || []).filter(
            (hC) => {
              const doFilter = !neededColumns.includes(hC);
              if (!doFilter) {
                newColumnOrder = [
                  ...newColumnOrder.filter((cO) => cO !== hC),
                  hC,
                ];
              }
              return doFilter;
            },
          );
          setColumnOrder(newColumnOrder);
          setHiddenColumns(newHiddenColumns);
        }
      },
    });
    if (filterData) setFilter(filterData);
  };

  const onPageClick = (pgNo: number) => {
    // setAllSelected(false);
    executeSegment({
      rule,
      params: {
        page: pgNo,
        per_page: COTACTS_PER_PAGE,
      },
    });
  };

  const { data: allFilterViews } = useGetFilterViews({
    filter_view_type: 'CONTACT',
  });

  React.useEffect(() => {
    if (
      !shouldFetchAllUsers &&
      ['creater_id', 'updater_id'].some(
        (cName) => !hiddenColumns.includes(cName),
      )
    ) {
      setShouldFetchAllUsers(true);
    }
  }, [hiddenColumns]);

  React.useEffect(() => {
    if (!actionTime) return;
    onFiltersApply();
  }, [actionTime]);

  React.useEffect(() => {
    onPageClick(pageIndex + 1);
  }, []);

  React.useEffect(() => {
    setselectedFilter(
      DefaultFilterViews.find((filter) => filter.id == filterViewId) ||
        allFilterViews?.filter_view_list?.find(
          (filter) => filter.id == filterViewId,
        ) ||
        DefaultFilterViews[0],
    );
    allFilterViews?.filter_view_list?.map((filter: FilterViewType) => {
      return { ...filter, conditions: filter?.conditions };
    });
  }, [allFilterViews]);

  const count = React.useMemo(() => meta.totalNumberOfRecords || 0, [meta]);

  const selectedRowIdsArray = React.useMemo(
    () => Object.keys(selectedRowIds),
    [selectedRowIds],
  );

  const showSelectionInfo = React.useMemo(() => {
    const currentPageContacts = {};
    segmentData?.forEach((c) => {
      currentPageContacts[c.id || ''] = c;
    });
    return (
      (selectedRowIdsArray.some((i) => !currentPageContacts[i]) ||
        selectedRowIdsArray.length >= COTACTS_PER_PAGE) &&
      count > COTACTS_PER_PAGE
    );
  }, [segmentData, selectedRowIdsArray, count]);

  return (
    <TableDataContainer className="contacts-container">
      <Table
        disableSelectMenuOptions
        data={segmentData || []}
        columns={columns}
        infoComponent={
          showSelectionInfo ? (
            <SelectAllContainer>
              {t('lists.contact_list.select_contacts', {
                count: isAllSelected ? count : selectedRowIdsArray.length,
              })}
              &nbsp;
              {!isAllSelected && count !== selectedRowIdsArray.length && (
                <FreshLink
                  onClick={() => {
                    setAllSelected(true);
                    setSelectedRowIds((old) => {
                      const newValues = { ...old };
                      (segmentData || []).forEach((c: McrContactType) => {
                        if (c.id) newValues[c.id] = true;
                      });
                      return newValues;
                    });
                  }}
                >
                  {t('lists.contact_list.all_contact', {
                    count: count,
                  })}
                </FreshLink>
              )}
              {isAllSelected && (
                <>
                  &nbsp;
                  <FreshLink
                    onClick={() => {
                      setAllSelected(false);
                      setSelectedRowIds({});
                    }}
                  >
                    {t('contacts.clear_selection')}
                  </FreshLink>
                </>
              )}
            </SelectAllContainer>
          ) : undefined
        }
        onRowSelect={(row, checked) => {
          if (!checked) setAllSelected(false);
        }}
        hideTableOnEmpty
        count={count}
        isLoading={!segmentData && !isExecuteSegmentError}
        loaderStyles={{ minHeight: 'calc(100vh - 136px)' }}
        disableSelectMenuOptionIds={isAllSelected ? ['SELECT_ALL'] : []}
        selectMenuOptionsHandler={(val) => {
          if (val.id === 'SELECT_ALL') setAllSelected(true);
        }}
        pageIndex={pageIndex}
        isFetching={segmentData ? isExecutingSegment : false}
        pageSize={COTACTS_PER_PAGE}
        hiddenColumns={hiddenColumns}
        selectedRowIds={selectedRowIds}
        disableRowSelect={access.indexOf('contact:write') === -1}
        columnOrder={columnOrder}
        setSelectedRowIds={setSelectedRowIds}
        setPageIndex={(pgIdx: number) => {
          onPageClick(pgIdx + 1);
          setPageIndex(pgIdx);
        }}
        cellPropGetter={(cell: any) => {
          const field = allFields[cell.column.id] || {};
          return {
            className: get(field, ['name'], ''),
            title:
              field && field.name && field.visible
                ? get(cell, ['row', 'original', field?.name])
                : '',
          };
        }}
        onRowClick={(row: any, column: any) => {
          const field = allFields[column.id] || {};
          if (field && ON_CLICK_OPEN_COLUMNS.includes(field.name)) {
            push(`/contacts/${get(row, ['original', 'id'], '')}`);
          }
        }}
        onMenuOptionSelect={(option: string, rowData: any) => {
          switch (option) {
            case 'view':
              push(`/contacts/${rowData.id}`);
              break;
            case 'edit':
              setEditData(rowData);
              break;
            case 'delete':
              setConfirmationData({
                id: new Date().toISOString(),
                title: `${t('contacts.menu.delete.title')}`,
                className: 'error',
                confirmButtonText: `${t('delete')}`,
                onConfirm: () => {
                  setConfirmationData((old) => ({
                    ...old,
                    confirmButtonDisabled: true,
                  }));
                  deleteContact(rowData.id);
                },
                data: rowData,
                fill: '#d7461f',
                infoText: `${t('contacts.menu.delete.contact_infotext')}`,
                description: (
                  <>
                    <Trans
                      i18nKey="contacts.menu.delete.desc"
                      values={{
                        email: rowData.work_email,
                      }}
                      components={{ bold: <b /> }}
                    />
                  </>
                ),
                glyph: 'info',
              });

              break;
            case 'deactivate':
              setConfirmationData({
                id: new Date().toISOString(),
                confirmButtonText: `${t('contacts.menu.deactivate.text')}`,
                onConfirm: () => {
                  setConfirmationData((old) => ({
                    ...old,
                    confirmButtonDisabled: true,
                  }));
                  saveContact({
                    payload: {
                      ...rowData,
                      fields: {
                        ...(rowData.fields || {}),
                        contact_status: 'IN_ACTIVE',
                      },
                    },
                  });
                },
                title: `${t('contacts.menu.deactivate.title')}`,
                description: (
                  <>
                    <Trans
                      i18nKey="contacts.menu.deactivate.desc"
                      values={{
                        email: rowData.work_email,
                      }}
                      components={{ bold: <b /> }}
                    />
                  </>
                ),
              });
              break;
            case 'activate':
              setConfirmationData({
                id: new Date().toISOString(),
                confirmButtonText: `${t('contacts.menu.activate.text')}`,
                onConfirm: () => {
                  setConfirmationData((old) => ({
                    ...old,
                    confirmButtonDisabled: true,
                  }));
                  saveContact({
                    payload: {
                      ...rowData,
                      fields: {
                        ...(rowData.fields || {}),
                        contact_status: 'ACTIVE',
                      },
                    },
                  });
                },
                title: `${t('contacts.menu.activate.title')}`,
                description: (
                  <>
                    <Trans
                      i18nKey="contacts.menu.activate.desc"
                      values={{
                        email: rowData.work_email,
                      }}
                      components={{ bold: <b /> }}
                    />
                  </>
                ),
              });
              break;
            // case 'block':
            //   setConfirmationData({
            //     id: new Date().toISOString(),
            //     confirmButtonText: 'Block',
            //     onConfirm: () => {
            //       setConfirmationData((old) => ({
            //         ...old,
            //         confirmButtonDisabled: true,
            //       }));
            //       saveContact({
            //         payload: {
            //           ...rowData,
            //           fields: {
            //             ...(rowData.fields || {}),
            //             is_blocked: true,
            //           },
            //         },
            //       });
            //     },
            //     title: 'Confirm block',
            //     description: `Do you want to block ${rowData.work_email}?`,
            //   });
            //   break;
            // case 'unblock':
            //   setConfirmationData({
            //     id: new Date().toISOString(),
            //     confirmButtonText: 'Unblock',
            //     onConfirm: () => {
            //       setConfirmationData((old) => ({
            //         ...old,
            //         confirmButtonDisabled: true,
            //       }));
            //       saveContact({
            //         payload: {
            //           ...rowData,
            //           fields: {
            //             ...(rowData.fields || {}),
            //             is_blocked: false,
            //           },
            //         },
            //       });
            //     },
            //     title: 'Confirm unblock',
            //     description: `Do you want to unblock ${rowData.work_email}?`,
            //   });
            //   break;
            case 'unsubscribe':
              setConfirmationData({
                id: new Date().toISOString(),
                confirmButtonText: `${t('contacts.menu.unsubscribe.text')}`,
                onConfirm: () => {
                  setConfirmationData((old) => ({
                    ...old,
                    confirmButtonDisabled: true,
                  }));
                  saveContact({
                    payload: {
                      ...rowData,
                      fields: {
                        ...(rowData.fields || {}),
                        is_unsubscribed: true,
                        unsubscribe_time: new Date().toISOString(),
                      },
                    },
                  });
                },
                title: `${t('contacts.menu.unsubscribe.title')}`,
                description: (
                  <>
                    <Trans
                      i18nKey="contacts.menu.unsubscribe.desc"
                      values={{
                        email: rowData.work_email,
                      }}
                      components={{ bold: <b /> }}
                    />
                  </>
                ),
              });
              break;
            case 'subscribe':
              setConfirmationData({
                id: new Date().toISOString(),
                confirmButtonText: `${t('contacts.menu.subscribe.text')}`,
                onConfirm: () => {
                  setConfirmationData((old) => ({
                    ...old,
                    confirmButtonDisabled: true,
                  }));
                  saveContact({
                    payload: {
                      ...rowData,
                      fields: {
                        ...(rowData.fields || {}),
                        is_unsubscribed: false,
                        unsubscribe_time: null,
                      },
                    },
                  });
                },
                title: `${t('contacts.menu.subscribe.title')}`,
                description: (
                  <>
                    <Trans
                      i18nKey="contacts.menu.subscribe.desc"
                      values={{
                        email: rowData.work_email,
                      }}
                      components={{ bold: <b /> }}
                    />
                  </>
                ),
              });
              break;
            case 'queueIn':
              break;
          }
        }}
        headerComponent={
          <HeaderComponent
            contactLists={contactLists}
            executeSegment={onFiltersApply}
            isAllSelected={isAllSelected}
            filterViewCount={filterViewCount}
            filterViewComponent={
              <ContactsDefaultFilterView
                filters={filter}
                filterViewCount={filterViewCount}
                onChange={onDefaultFilterViewChange}
                onDelete={onDeleteFilterView}
                onSave={onSaveFilterView}
                onClearFilterView={clearFilterView}
                filteredViews={
                  allFilterViews ? allFilterViews.filter_view_list : []
                }
                selectedFilter={selectedFilter}
                isFilterConditionsModified={isFilterConditionsModified}
                setisFilterConditionsModified={setisFilterConditionsModified}
              />
            }
            type="contact"
            rule={rule}
            showSearchInActions
            columnOrder={columnOrder}
            setSelectedRowIds={setSelectedRowIds}
            setConfirmationData={setConfirmationData}
            defaultFilterCondition={getDefaultCondition(
              accountInfo?.mcr_account_id || '',
            )}
            showNewContact
            filter={filter}
            selectedRows={
              uniq(selectedRowIdsArray).map(
                (contactId) =>
                  queryClient.getQueryData(['contact', contactId]) ||
                  allContacts[contactId] ||
                  {},
              ) || []
            }
            setColumnOrder={(cols: string[]) => setColumnOrder(cols)}
            hiddenColumns={hiddenColumns}
            setHiddenColumns={(cols: string[]) => setHiddenColumns(cols)}
            fields={fields}
            columnSettings={columnSettings}
            accountInfo={accountInfo}
            userInfo={userInfo}
            contactsCount={contactsCount}
          />
        }
        filterComponent={
          <Filters
            // hideFields={(
            //   DefaultFilterViews.find(
            //     (defaultFilterView) => defaultFilterView.id === filterViewId,
            //   )?.conditions || []
            // ).map((condition) => condition.name)}
            columnSettings={columnSettings}
            fields={fields}
            rule={segmentRule}
            onClearFilterView={clearFilterView}
            selectedFilter={selectedFilter}
            filter={filter}
            onFilter={onFiltersApply}
            userInfo={userInfo}
            isFilterConditionsModified={isFilterConditionsModified}
          />
        }
        emptyComponent={
          <EmptyState
            minHeight="calc(100vh - 290px)"
            title={`${t('empty_state.title.no_contacts_to_display')}`}
            message={
              isExecuteSegmentError
                ? `${t('empty_state.message.network_error')}`
                : `${t('empty_state.message.click_to_add_contacts')}`
            }
            imgSrc="empty-state-contacts.svg"
          >
            {(access || []).indexOf('contact:write') > -1 && (
              <Button inline onClick={() => push('/contacts?open=contact')}>
                + {t('new_contact')}
              </Button>
            )}
          </EmptyState>
        }
        rowMenuOptions={(contact: McrContactType) => {
          return getContactMenuOptions(contact, [], access);
        }}
      />
      <Drawer
        isOpen={Boolean(editData.id)}
        onDismiss={() => {
          if (isDirty) {
            setConfirmationData({
              id: new Date().toISOString(),
              title: `${t('confirm_close')}`,
              onConfirm: () => {
                setEditData({});
                setConfirmationData({});
              },
              description: `${t('contacts.contacts_changes_not_saved')}`,
            });
          } else {
            setEditData({});
          }
        }}
        isFullHeight
        clickOutToClose={false}
        size="large"
        placement="right"
      >
        <NewContactForm
          fields={fields}
          setIsDirty={(d: boolean) => setIsDirty(d)}
          executeSegment={onFiltersApply}
          values={editData}
          toggleDrawer={() => setEditData({})}
        />
      </Drawer>
    </TableDataContainer>
  );
};

export default Contacts;
